/**
 * SSO import desde Web (OPERATOR #4, 2026-06-22).
 *
 * Web (First Class storefront, :6010 / soyfc.com) y Mi Boleta (este repo,
 * :6012 / miboleta.firstclass.com) consumen el MISMO backend (api-core /
 * api-taquilla, mismo Supabase ref — ADR-T01 Strangler Fig). El JWT que emite
 * api-core para el usuario logueado en Web es VÁLIDO acá: la forma de la sesión
 * (`AuthSession = { user, tokens }`) es idéntica en ambos `tokenStore.ts`.
 *
 * El único motivo por el que la sesión no viaja sola es que son orígenes
 * distintos → `localStorage` aislado (`fc-web-auth` vs `fc-commerce-auth`).
 *
 * Web, al hacer "Ir a pagar", construye:
 *
 *   {COMMERCE_URL}/cart?fc_cart=<base64url(json)>#fcsess=<base64url(json)>
 *
 * Acá, ANTES de montar React, leemos el fragmento `#fcsess=...` y, si trae una
 * sesión válida, hacemos `setSession(...)` (rehidrata el tokenStore +
 * dispara SIGNED_IN). Luego borramos el fragmento de la URL (history.replace)
 * para que el token no quede visible ni se comparta al copiar el link.
 *
 * Notas de seguridad:
 *   - El fragmento (`#...`) NO viaja al servidor (no aparece en access logs ni
 *     en el header Referer), a diferencia de un query param. Es el canal
 *     correcto para un token de un solo uso en un handoff cliente→cliente.
 *   - El token es el access token normal de api-core (TTL corto, 15m). Si está
 *     vencido cuando Mi Boleta lo use, el interceptor de `http.ts` lo rota con
 *     el refresh token, igual que en una sesión nativa.
 */

import { setSession, type AuthSession } from '@/lib/api';

const FRAGMENT_KEY = 'fcsess';

/** base64url-decode → string UTF-8. */
function b64urlDecode(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  // padding
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  return decodeURIComponent(escape(atob(b64 + pad)));
}

/** Valida que el payload parseado tenga la forma mínima de `AuthSession`. */
function isValidSession(p: unknown): p is AuthSession {
  if (!p || typeof p !== 'object') return false;
  const s = p as Partial<AuthSession> & { tokens?: Partial<AuthSession['tokens']> };
  return (
    !!s.user &&
    typeof s.user.id === 'string' &&
    typeof s.user.email === 'string' &&
    !!s.tokens &&
    typeof s.tokens.access_token === 'string' &&
    typeof s.tokens.refresh_token === 'string'
  );
}

/**
 * Procesa el fragmento `#fcsess=` si está presente. Idempotente: tras leerlo,
 * limpia el fragmento de la URL. Llamar UNA vez en el boot (antes de render).
 *
 * @returns true si importó una sesión (Web→Mi Boleta SSO ok), false si no había.
 */
export function importSessionFromHandoff(): boolean {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash; // ej. "#fcsess=eyJ..."
  if (!hash || hash.length < 2) return false;

  // El hash puede tener varios pares (#a=1&fcsess=...). Lo parseamos.
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(raw);
  const token = params.get(FRAGMENT_KEY);
  if (!token) return false;

  let imported = false;
  try {
    const parsed = JSON.parse(b64urlDecode(token)) as unknown;
    if (isValidSession(parsed)) {
      setSession({ user: parsed.user, tokens: parsed.tokens });
      imported = true;
    }
  } catch {
    /* payload corrupto — ignoramos y seguimos sin sesión (Mi Boleta pedirá login) */
  }

  // Limpiar el fragmento SIEMPRE (haya importado o no) para no dejar el token
  // en la barra de direcciones / historial. Conservamos cualquier OTRO par del
  // fragmento que no sea `fcsess`.
  params.delete(FRAGMENT_KEY);
  const remaining = params.toString();
  const newHash = remaining ? `#${remaining}` : '';
  const newUrl = `${window.location.pathname}${window.location.search}${newHash}`;
  try {
    window.history.replaceState(window.history.state, '', newUrl);
  } catch {
    /* en contextos sin history (tests) no es crítico */
  }

  return imported;
}
